import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ReasonField } from "@/components/ReasonField";

const BAN_REASON_MIN_LENGTH = 20;

type BanStep = "form" | "confirm" | "submitting" | "error";

interface BanModalProps {
  open: boolean;
  userId: string;
  userEmail?: string;
  onBan: (params: { reason: string }) => Promise<void>;
  onClose: () => void;
}

function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError && err.message.toLowerCase().includes("fetch");
}

export function BanModal({
  open,
  userId,
  userEmail,
  onBan,
  onClose,
}: BanModalProps): React.JSX.Element {
  const [step, setStep] = React.useState<BanStep>("form");
  const [reason, setReason] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setStep("form");
      setReason("");
      setErrorMessage("");
    }
  }, [open]);

  const reasonValid = reason.length >= BAN_REASON_MIN_LENGTH;
  const isSubmitting = step === "submitting";

  function handleNext(): void {
    if (reasonValid) {
      setStep("confirm");
    }
  }

  async function handleConfirm(): Promise<void> {
    setStep("submitting");
    setErrorMessage("");
    try {
      await onBan({ reason });
      onClose();
    } catch (err: unknown) {
      if (isNetworkError(err)) {
        setErrorMessage("Нет соединения, повторите");
      } else {
        const bffErr = err as { code?: string; message?: string } | null;
        const mapped = mapBanErrorCode(bffErr?.code);
        setErrorMessage(mapped ?? "Ошибка сервера. Попробуйте ещё раз.");
      }
      setStep("error");
    }
  }

  function handleCancel(): void {
    if (step === "confirm") {
      setStep("form");
    } else {
      onClose();
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {step === "confirm"
              ? "Подтвердите блокировку"
              : "Заблокировать пользователя"}
          </DialogTitle>
        </DialogHeader>

        {step === "confirm" ? (
          <div className="py-1 flex flex-col gap-3">
            <dl className="bg-[var(--surface-2)] border border-[var(--border)] rounded-[var(--radius-sm)] p-3.5 text-[13px]">
              <dt className="text-[11px] text-[var(--muted)] mb-1">Пользователь</dt>
              <dd className="mb-3 font-[500]">{userEmail ?? userId}</dd>
              <dt className="text-[11px] text-[var(--muted)] mb-1">Причина</dt>
              <dd className="mb-3">{reason}</dd>
              <dt className="text-[11px] text-[var(--muted)] mb-1">Сессии</dt>
              <dd>Все активные сессии будут немедленно завершены</dd>
            </dl>
            <div
              role="alert"
              className="flex items-start gap-2 px-3.5 py-2.5 bg-[rgba(196,58,42,0.06)] border border-[rgba(196,58,42,0.18)] rounded-[var(--radius-sm)] text-[12px] text-[var(--fg)]"
            >
              <span className="text-[var(--danger)] flex-shrink-0" aria-hidden="true">⚠</span>
              <span>
                <strong>Это действие необратимо.</strong> Пользователь будет заблокирован и потеряет доступ ко всем данным в приложении.
              </span>
            </div>
          </div>
        ) : (
          <div className="py-1 flex flex-col gap-3">
            <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-[var(--radius-sm)] p-3 text-[13px]">
              <div className="font-[500]">{userEmail ?? "—"}</div>
              <div className="text-[11px] text-[var(--muted)] font-mono mt-0.5">{userId}</div>
            </div>

            <ReasonField
              id="ban-reason"
              label="Причина блокировки"
              value={reason}
              onChange={setReason}
              minLength={BAN_REASON_MIN_LENGTH}
              required
              placeholder="Минимум 20 символов..."
              hint="Минимум 20 символов. Причина будет видна в Audit Log."
            />

            <div
              className="flex items-start gap-2 px-3.5 py-2.5 bg-[rgba(196,138,26,0.06)] border border-[rgba(196,138,26,0.18)] rounded-[var(--radius-sm)] text-[12px] text-[var(--fg)]"
            >
              <span className="text-[var(--warn)] flex-shrink-0" aria-hidden="true">⚠</span>
              <span>
                Все активные сессии пользователя будут немедленно завершены.
                Пользователь потеряет доступ к приложению.
              </span>
            </div>

            {(step === "error") && errorMessage && (
              <div
                role="alert"
                className="flex items-start gap-2 px-3.5 py-2.5 bg-[rgba(196,58,42,0.08)] border border-[rgba(196,58,42,0.2)] rounded-[var(--radius-sm)] text-[12px] text-[var(--danger)]"
              >
                <span>✕</span>
                <span>{errorMessage}</span>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-4 mt-2 border-t border-[var(--border)]">
          <span className="text-[11px] text-[var(--muted)] flex-1 leading-relaxed pr-3">
            Зафиксируется в Audit Log
          </span>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Отмена
            </Button>
            {step === "confirm" ? (
              <Button
                type="button"
                variant="destructive"
                onClick={() => { void handleConfirm(); }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Загрузка..." : (
                  <>
                    <span aria-hidden="true">⚠</span>
                    {" "}Да, заблокировать
                  </>
                )}
              </Button>
            ) : (
              <Button
                type="button"
                variant="destructive"
                onClick={handleNext}
                disabled={!reasonValid || isSubmitting}
              >
                Далее
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function mapBanErrorCode(code: string | undefined): string | null {
  if (code === "admin.user_not_found") return "Пользователь не найден.";
  if (code === "admin.already_taken_down") return "Пользователь уже заблокирован.";
  return null;
}
